/**
 * CommentThread — renders a nested/threaded comment tree.
 *
 * Receives a flat list of comments from the API and builds a tree
 * client-side based on parentId. Each comment can have a "Reply" button
 * that triggers the parent's reply handler.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Reply, User, Clock } from 'lucide-react';
import { tr } from '../../i18n/standalone';
import type { Comment } from '../../types';
import './CommentThread.css';

interface CommentThreadProps {
  comments: Comment[];
  onReply: (parentId: number) => void;
}

/** Build a tree from a flat list of comments. */
function buildTree(flat: Comment[]): Comment[] {
  const map = new Map<number, Comment>();
  const roots: Comment[] = [];

  // First pass: clone each comment with an empty children array.
  for (const c of flat) {
    map.set(c.id, { ...c, children: [] });
  }

  // Second pass: attach children to their parents.
  for (const c of map.values()) {
    if (c.parentId !== null && map.has(c.parentId)) {
      map.get(c.parentId)!.children!.push(c);
    } else {
      roots.push(c);
    }
  }

  return roots;
}

/**
 * Format a date string into a human-readable relative or absolute format.
 *
 * The relative units are separate keys rather than a number plus a suffix:
 * French wants "il y a 5 min", where the parts sit on the other side of the
 * number.
 *
 * The absolute fallback deliberately stays en-US. Number and date formatting is
 * NOT localized anywhere in this app — a product decision (2026-09-01): the
 * market is the Americas, where only fr-CA differs from the US convention
 * (es-MX formats identically to en-US), and the inconsistency is not worth the
 * sweep. Don't "fix" this one in isolation.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr.replace(' ', 'T')); // stored in server-local time, parse as local
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return tr('results:comments.time.justNow');
  if (diffMin < 60) return tr('results:comments.time.minutes', { count: diffMin });
  if (diffHr < 24) return tr('results:comments.time.hours', { count: diffHr });
  if (diffDay < 7) return tr('results:comments.time.days', { count: diffDay });

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/** Get initials from a name for the avatar. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (name[0] || 'G').toUpperCase();
}

/** Generate a deterministic hue from a name string. */
function nameToHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

const SingleComment: React.FC<{
  comment: Comment;
  depth: number;
  onReply: (parentId: number) => void;
}> = ({ comment, depth, onReply }) => {
  const { t } = useTranslation('results');
  const hue = nameToHue(comment.authorName);
  const avatarBg = `hsl(${hue}, 45%, 92%)`;
  const avatarColor = `hsl(${hue}, 55%, 35%)`;

  return (
    <div className={`comment-node ${depth > 0 ? 'comment-node--reply' : ''}`}>
      <div className="comment-card">
        <div className="comment-header">
          <div
            className="comment-avatar"
            style={{ background: avatarBg, color: avatarColor }}
          >
            {comment.userId ? (
              <User size={14} />
            ) : (
              getInitials(comment.authorName)
            )}
          </div>
          <div className="comment-meta">
            <span className="comment-author">{comment.authorName}</span>
            {!comment.userId && (
              <span className="comment-badge">{t('comments.guest')}</span>
            )}
          </div>
          <div className="comment-time">
            <Clock size={12} />
            <span>{formatDate(comment.createdAt)}</span>
          </div>
        </div>

        <div
          className="comment-body"
          dangerouslySetInnerHTML={{ __html: comment.commentText }}
        />

        <div className="comment-actions">
          <button
            type="button"
            className="comment-reply-btn"
            onClick={() => onReply(comment.id)}
          >
            <Reply size={13} />
            {t('comments.reply')}
          </button>
        </div>
      </div>

      {comment.children && comment.children.length > 0 && (
        <div className="comment-children">
          {comment.children.map(child => (
            <SingleComment
              key={child.id}
              comment={child}
              depth={depth + 1}
              onReply={onReply}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const CommentThread: React.FC<CommentThreadProps> = ({
  comments,
  onReply,
}) => {
  const { t } = useTranslation('results');
  const tree = buildTree(comments);

  if (tree.length === 0) {
    return (
      <div className="comment-empty">
        <MessageSquare size={40} strokeWidth={1.2} />
        <p className="comment-empty-title">{t('comments.emptyTitle')}</p>
        <p className="comment-empty-sub">{t('comments.emptyBody')}</p>
      </div>
    );
  }

  return (
    <div className="comment-thread">
      {tree.map(comment => (
        <SingleComment
          key={comment.id}
          comment={comment}
          depth={0}
          onReply={onReply}
        />
      ))}
    </div>
  );
};
